import { Box, Button, Checkbox, Flex, Grid, GridItem, Spinner } from '@chakra-ui/react';
import CButton from 'components/CButton';
import { usePermission } from 'contexts/PermissionContext';
import React, { useEffect, useState } from 'react';

// const roles = ['admin', 'receptionist', 'trainer'];
const roles = [
  { name: 'Admin', value: 'admin' }, 
  { name: 'Receptionist', value: 'receptionist' }, 
  { name: 'Trainer', value: 'trainer' }
];

// const modules = ['staff', 'members', 'locations', 'services', 'discounts', 'locks', 'audits'];
const modules = [
  { name: 'Staff', value: 'staff' }, 
  { name: 'Members', value: 'members' }, 
  { name: 'Locations', value: 'locations' }, 
  { name: 'Services', value: 'services' }, 
  { name: 'Discounts', value: 'discounts' }, 
  { name: 'Locks', value: 'locks' }, 
  { name: 'Notifications', value: 'notifications' }
];

const Permissions = () => {
  const { getAllPermissions, updatePermissions } = usePermission();

  const [isLoading, setIsLoading] = useState(false);
  const [permissions, setPermissions] = useState(() =>
    roles.reduce((acc, role) => {
      acc[role.value] = modules.reduce((modAcc, mod) => {
        modAcc[mod.value] = { create: false, read: false, update: false, delete: false };
        return modAcc;
      }, {});
      return acc;
    }, {})
  );

  const togglePermission = (role, module, action) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: {
          ...prev[role][module],
          [action]: !prev[role][module][action],
        },
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      await updatePermissions(permissions);
    } catch (error) {
      console.log("Error updating the Permissions", error.message);
    }
  };

  const fetchAllPermissions = async () => {
    setIsLoading(true);
    try {
      const data = await getAllPermissions();
      if (data) {
        setPermissions(data);
      }
    } catch (error) {
      console.log("Error in Permissions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPermissions();
  }, []);

  if(isLoading) {
    return <Box direction='column' pt={{ sm: "125px", lg: "80px" }} pl={"7px"}>
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius="10px"
            overflow="hidden"
            minHeight="600px"
        >
            <Spinner color="#fa8f03" />
        </Box>
      </Box>
  }

  return (
    <Box direction='column' pt={{ sm: "125px", lg: "80px" }} pl={"7px"}>
      <Grid
        templateColumns={`250px repeat(${roles.length}, 1fr)`}
        gap={4}
        borderRadius="md"
        overflow="hidden"
      >
        {/* Header Row */}
        <GridItem bg="gray.200" fontWeight="bold" p={2} textAlign="center">
          Modules
        </GridItem>
        {roles.map(role => (
          <GridItem key={role.name} bg="gray.200" fontWeight="bold" p={2} textAlign="center">
            {role.name}
          </GridItem>
        ))}

        {/* Actions Row */}
        <GridItem bg="gray.100" fontWeight="bold" p={2} textAlign="center">
          Actions
        </GridItem>
        {roles.map(role => (
          <GridItem key={`${role.name}-actions`} bg="gray.100" fontWeight="bold" p={2} textAlign="center">
            Create | Read | Update | Delete
          </GridItem>
        ))}

        {/* Module Rows */}
        {modules.map(module => (
          <React.Fragment key={module.name}>
            <GridItem p={4} fontWeight="medium" borderBottom="1px solid #e2e8f0" textAlign="start">
              {module.name}
            </GridItem>
            {roles.map(role => (
              <GridItem
                key={`${module.name}-${role.name}`}
                p={2}
                borderBottom="1px solid #e2e8f0"
                textAlign="center"
              >
                <Flex gap={10} justify="center" align="center">
                  {['create', 'read', 'update', 'delete'].map(action => (
                    <Checkbox
                      title={action.toUpperCase()}
                      key={`${module.name}-${role.name}-${action}`}
                      isChecked={permissions[role.value][module.value][action]}
                      className={`${(action !== 'read' && !permissions[role.value][module.value]['read']) ? 'disabled-checkbox' : ''}`}
                      isDisabled={
                        action !== 'read' && !permissions[role.value][module.value]['read']
                      }
                      onChange={() => togglePermission(role.value, module.value, action)}
                      colorScheme={"orange"}
                    />
                  ))}
                </Flex>
              </GridItem>
            ))}
          </React.Fragment>
        ))}
      </Grid>

      <Flex justify="flex-end" align="center" mt="44px" gap="3">
        <CButton
          fontSize="sm"
          borderRadius="16px"
          w={{ base: '128px', md: '148px' }}
          h="46px"
          type="button"
          onClick={handleSubmit}
        >
          Submit
        </CButton>
      </Flex>
    </Box>
  );
};

export default Permissions;